
import { BASE_URL, UNSPLASH_ACCESS_KEY } from './constants';
import { getFromCache, saveToCache } from './cache';
import { getFallbackImage } from './fallback';

/**
 * Get an image from Unsplash by ID
 */
export const getUnsplashImageById = async (photoId: string): Promise<string> => {
  try {
    // Check cache first
    const cachedImage = getFromCache(photoId);
    if (cachedImage) return cachedImage;

    // Fetch from API if not in cache
    const response = await fetch(`${BASE_URL}/photos/${photoId}?client_id=${UNSPLASH_ACCESS_KEY}`);
    
    if (!response.ok) {
      console.error(`Unsplash API error: ${response.status}`);
      throw new Error(`Unsplash API error: ${response.status}`);
    }
    
    const data = await response.json();
    const imageUrl = data.urls.regular;
    
    // Save to cache
    saveToCache(photoId, imageUrl);
    
    // Track photo usage as per Unsplash API guidelines
    fetch(`${BASE_URL}/photos/${photoId}/download?client_id=${UNSPLASH_ACCESS_KEY}`);
    
    return imageUrl;
  } catch (error) {
    console.error('Error fetching Unsplash image by ID:', error);
    return getFallbackImage('default');
  }
};

/**
 * Search for an image on Unsplash
 */
export const searchUnsplashImage = async (query: string): Promise<string> => {
  try {
    // Check cache first
    const cacheKey = `search:${query}`;
    const cachedImage = getFromCache(cacheKey);
    if (cachedImage) return cachedImage;

    // Fetch from API if not in cache
    const response = await fetch(
      `${BASE_URL}/search/photos?query=${encodeURIComponent(query)}&per_page=1&client_id=${UNSPLASH_ACCESS_KEY}`
    );
    
    if (!response.ok) {
      console.error(`Unsplash API error: ${response.status}`);
      throw new Error(`Unsplash API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.results && data.results.length > 0) {
      const imageUrl = data.results[0].urls.regular;
      
      // Save to cache
      saveToCache(cacheKey, imageUrl);
      
      // Track photo download as per Unsplash API guidelines
      const photoId = data.results[0].id;
      fetch(`${BASE_URL}/photos/${photoId}/download?client_id=${UNSPLASH_ACCESS_KEY}`);
      
      return imageUrl;
    }
    
    return getFallbackImage('default');
  } catch (error) {
    console.error('Error searching Unsplash image:', error);
    return getFallbackImage('default');
  }
};
